// @ts-check
import { ArrowLeft, Copy, Edit2, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Modal from '../components/ui/Modal';
import { useToast } from '../context/ToastContext';
import { useRegisterUnsavedChanges } from '../context/UnsavedChangesContext';
import adminMaterialService from '../services/adminMaterialService';
import { adminProductVariantService } from '../services/adminProductExtrasService';
import adminProductService from '../services/adminProductService';
import atelierAdminService, { STAGES, STAGE_LABELS } from '../services/atelierAdminService';

const TABS = ['Overview', 'Materials & Costing', 'Quote & Invoices', 'Messages', 'Files'];

export default function AtelierProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [tab, setTab] = useState('Overview');
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadProject = useCallback(async () => {
    setLoading(true);
    try {
      setProject(await atelierAdminService.getProject(projectId));
    } catch (e) {
      toast.error(e.message || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [projectId, toast]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  if (!project) {
    return <div className="p-6 text-muted-foreground/70">{loading ? 'Loading…' : 'Not found'}</div>;
  }

  return (
    <div>
      <button
        onClick={() => navigate('/atelier/projects')}
        className="flex items-center gap-1 text-sm text-muted-foreground mb-3 hover:text-foreground"
      >
        <ArrowLeft size={16} /> Back to pipeline
      </button>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold">{project.title}</h1>
          <p className="text-sm text-muted-foreground font-mono">
            {project.reference_code} · {project.project_type?.replace(/_/g, ' ')} · qty {project.quantity} ·{' '}
            {project.country}
          </p>
        </div>
        <span className="px-3 py-1 rounded-full text-sm bg-muted">{STAGE_LABELS[project.stage] || project.stage}</span>
      </div>

      <div className="flex gap-1 border-b mb-5 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-shrink-0 whitespace-nowrap px-4 py-2 text-sm -mb-px border-b-2 ${tab === t ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground'}`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Overview' && <Overview project={project} onChanged={loadProject} />}
      {tab === 'Materials & Costing' && <MaterialsCosting project={project} onChanged={loadProject} />}
      {tab === 'Quote & Invoices' && <QuoteInvoices project={project} onChanged={loadProject} />}
      {tab === 'Messages' && <Messages projectId={projectId} />}
      {tab === 'Files' && <Files projectId={projectId} />}
    </div>
  );
}

function SourceProduct({ productId }) {
  const [product, setProduct] = useState(null);
  const [wholesale, setWholesale] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    adminProductService
      .getProduct(productId)
      .then(setProduct)
      .catch(() => setNotFound(true));
    atelierAdminService
      .listWholesale()
      .then((rows) => {
        setWholesale((rows || []).find((r) => r.product_id === productId) || null);
      })
      .catch(() => {});
  }, [productId]);

  return (
    <section className="bg-card border rounded-lg p-4">
      <h3 className="font-medium mb-2">Reference product (own design)</h3>
      {notFound ? (
        <p className="text-sm text-red-600 dark:text-red-400">Product not found (id {productId}).</p>
      ) : !product ? (
        <p className="text-sm text-muted-foreground/70">Loading…</p>
      ) : (
        <div className="flex gap-3 items-center">
          {(product.image_urls || [])[0] && (
            <img src={product.image_urls[0]} alt={product.name} className="w-16 h-20 object-cover rounded-lg" />
          )}
          <div className="text-sm">
            <div className="font-medium">{product.name}</div>
            <div className="text-muted-foreground">
              Wholesale on file: {wholesale ? `₹${wholesale.unit_price}` : 'not set — quote manually'}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function EditDetails({ project, onChanged }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(project.title);
  const [quantity, setQuantity] = useState(project.quantity);
  const [brief, setBrief] = useState(project.design_brief || '');
  // Touched-since-open flag — this section has no single formData object
  // (each field is its own useState), so each onChange marks dirty directly.
  const [isDirty, setIsDirty] = useState(false);
  useRegisterUnsavedChanges(open && isDirty);
  const save = async () => {
    try {
      await atelierAdminService.editProject(project.id, { title, quantity: Number(quantity), design_brief: brief });
      toast.success('Saved');
      onChanged();
      setIsDirty(false);
      setOpen(false);
    } catch (e) {
      toast.error(e.message || 'Save failed');
    }
  };
  // Cancel dismisses the section — confirm first if there's anything to lose.
  const handleCancelClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setIsDirty(false);
    setOpen(false);
  };
  if (!open)
    return (
      <button onClick={() => setOpen(true)} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
        Edit details
      </button>
    );
  return (
    <section className="bg-card border rounded-lg p-4 space-y-2">
      <h3 className="font-medium">Edit details</h3>
      <input
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
          setIsDirty(true);
        }}
        className="w-full border rounded-lg px-3 py-2 text-sm"
        placeholder="Title"
      />
      <input
        type="number"
        min={20}
        max={50}
        value={quantity}
        onChange={(e) => {
          setQuantity(e.target.value);
          setIsDirty(true);
        }}
        className="w-full border rounded-lg px-3 py-2 text-sm"
        placeholder="Quantity"
      />
      <textarea
        value={brief}
        onChange={(e) => {
          setBrief(e.target.value);
          setIsDirty(true);
        }}
        rows={3}
        className="w-full border rounded-lg px-3 py-2 text-sm"
        placeholder="Brief / notes"
      />
      <div className="flex gap-2">
        <button onClick={save} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm">
          Save
        </button>
        <button onClick={handleCancelClick} className="px-4 py-2 rounded-lg bg-muted text-sm">
          Cancel
        </button>
      </div>
    </section>
  );
}

function ProjectNotes({ projectId }) {
  const toast = useToast();
  const [notes, setNotes] = useState('');
  // Touched-since-load flag — this field has no discard/cancel affordance
  // (it's always visible), so it only needs to register with the app-wide
  // guard (blocks navigating away, refresh, etc.) rather than a local confirm.
  const [isDirty, setIsDirty] = useState(false);
  useRegisterUnsavedChanges(isDirty);
  useEffect(() => {
    atelierAdminService
      .getProjectNotes(projectId)
      .then((d) => setNotes(d?.notes || ''))
      .catch(() => {});
  }, [projectId]);
  const save = async () => {
    try {
      await atelierAdminService.setProjectNotes(projectId, notes);
      toast.success('Notes saved');
      setIsDirty(false);
    } catch (e) {
      toast.error(e.message || 'Save failed');
    }
  };
  return (
    <section className="bg-card border rounded-lg p-4">
      <h3 className="font-medium mb-2">
        Internal notes <span className="text-xs text-muted-foreground/70">(not visible to buyer)</span>
      </h3>
      <textarea
        value={notes}
        onChange={(e) => {
          setNotes(e.target.value);
          setIsDirty(true);
        }}
        rows={4}
        className="w-full border rounded-lg px-3 py-2 text-sm"
      />
      <button onClick={save} className="mt-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm">
        Save notes
      </button>
    </section>
  );
}

function Overview({ project, onChanged }) {
  const toast = useToast();
  const [timeline, setTimeline] = useState([]);
  const [toStage, setToStage] = useState('');
  const [presets, setPresets] = useState([]);
  const [remark, setRemark] = useState('');
  const [busy, setBusy] = useState(false);

  const loadTimeline = useCallback(async () => {
    try {
      setTimeline(await atelierAdminService.getTimeline(project.id));
    } catch {
      /* ignore */
    }
  }, [project.id]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  useEffect(() => {
    if (!toStage) {
      setPresets([]);
      return;
    }
    atelierAdminService
      .listStageReasons(toStage)
      .then((r) => setPresets(Array.isArray(r) ? r.filter((x) => x.is_active) : []))
      .catch(() => setPresets([]));
  }, [toStage]);

  const advance = async () => {
    if (!toStage || !remark.trim()) {
      toast.error('Pick a stage and enter a remark');
      return;
    }
    setBusy(true);
    try {
      await atelierAdminService.advanceStage(project.id, toStage, remark.trim());
      toast.success(`Moved to ${STAGE_LABELS[toStage]}`);
      setToStage('');
      setRemark('');
      onChanged();
      loadTimeline();
    } catch (e) {
      toast.error(e.message || 'Stage change rejected');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="space-y-4">
        {project.project_type === 'own_design' && project.source_product_id && (
          <SourceProduct productId={project.source_product_id} />
        )}

        <section className="bg-card border rounded-lg p-4">
          <h3 className="font-medium mb-2">
            {project.project_type === 'own_design' ? 'Customization notes' : 'Design brief'}
          </h3>
          <p className="text-sm whitespace-pre-wrap text-foreground/80">{project.design_brief || '—'}</p>
        </section>

        {project.requirements && Object.keys(project.requirements).length > 0 && (
          <section className="bg-card border rounded-lg p-4">
            <h3 className="font-medium mb-2">Requirements</h3>
            <dl className="text-sm space-y-1">
              {Object.entries(project.requirements).map(([k, v]) =>
                v ? (
                  <div key={k} className="flex gap-2">
                    <dt className="text-muted-foreground capitalize min-w-[120px]">{k.replace(/_/g, ' ')}</dt>
                    <dd className="whitespace-pre-wrap">{String(v)}</dd>
                  </div>
                ) : null,
              )}
            </dl>
          </section>
        )}

        <EditDetails project={project} onChanged={onChanged} />
        <ProjectNotes projectId={project.id} />

        <section className="bg-card border rounded-lg p-4">
          <h3 className="font-medium mb-3">
            Change stage <span className="text-xs text-muted-foreground/70">(remark required)</span>
          </h3>
          <select
            value={toStage}
            onChange={(e) => {
              setToStage(e.target.value);
              setRemark('');
            }}
            className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
          >
            <option value="">Select target stage…</option>
            {STAGES.filter((s) => s !== project.stage).map((s) => (
              <option key={s} value={s}>
                {STAGE_LABELS[s]}
              </option>
            ))}
          </select>
          {presets.length > 0 && (
            <select
              onChange={(e) => setRemark(e.target.value)}
              value={presets.some((p) => p.label === remark) ? remark : ''}
              className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
            >
              <option value="">Pick a preset reason…</option>
              {presets.map((p) => (
                <option key={p.id} value={p.label}>
                  {p.label}
                </option>
              ))}
            </select>
          )}
          <textarea
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="Reason for this change (required)"
            rows={2}
            className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
          />
          <button
            disabled={busy}
            onClick={advance}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-40"
          >
            Apply change
          </button>
        </section>
      </div>

      <section className="bg-card border rounded-lg p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="font-medium">Stage history</h3>
          <button onClick={loadTimeline} className="p-1 hover:bg-accent rounded-lg">
            <RefreshCw size={15} />
          </button>
        </div>
        <ol className="space-y-3">
          {timeline.map((e) => (
            <li key={e.id} className="border-l-2 border-border pl-3">
              <div className="text-sm font-medium">{STAGE_LABELS[e.to_stage] || e.to_stage}</div>
              <div className="text-xs text-muted-foreground">{e.remark}</div>
              <div className="text-xs text-muted-foreground/70">
                {e.actor_type} · {e.created_at ? new Date(e.created_at).toLocaleString() : ''}
              </div>
            </li>
          ))}
          {timeline.length === 0 && <li className="text-muted-foreground/70 text-sm">No history</li>}
        </ol>
      </section>
    </div>
  );
}

const MATERIAL_PROCESSES = [
  { value: '', label: 'Use material default' },
  { value: 'raw', label: 'Raw' },
  { value: 'printed', label: 'Printed' },
  { value: 'dyed', label: 'Dyed' },
  { value: 'embroidered', label: 'Embroidered' },
  { value: 'hand_painted', label: 'Hand-painted' },
  { value: 'other', label: 'Other' },
];

const EMPTY_MATERIAL_LINE = {
  material_id: '',
  process: '',
  quantity_per_unit: '',
  wastage_percentage: '0',
  unit_cost: '',
  currency: 'INR',
  exchange_rate: '1',
  customs_duty_percentage: '0',
  freight_cost_per_unit: '0',
  color_or_variant_note: '',
  lot_number: '',
  notes: '',
};

function MaterialsCosting({ project, onChanged }) {
  const toast = useToast();
  const [lines, setLines] = useState([]);
  const [summary, setSummary] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [targetMargin, setTargetMargin] = useState(project.target_margin_percentage ?? '');
  const [showModal, setShowModal] = useState(false);
  const [editingLineId, setEditingLineId] = useState(null);
  // Touched-since-open flag, reset by resetForm-equivalents (openCreate/openEdit
  // populate via the raw setter so loading a line for editing never itself
  // counts as an edit).
  const [isDirty, setIsDirty] = useState(false);
  const [form, setFormRaw] = useState(EMPTY_MATERIAL_LINE);
  const setForm = useCallback((updater) => {
    setIsDirty(true);
    setFormRaw(updater);
  }, []);
  const [saving, setSaving] = useState(false);
  const [sourceVariants, setSourceVariants] = useState([]);
  const [copyVariantId, setCopyVariantId] = useState('');

  const load = useCallback(async () => {
    try {
      const [lineData, summaryData, materialData] = await Promise.all([
        atelierAdminService.listProjectMaterials(project.id),
        atelierAdminService.getProjectMaterialsSummary(project.id),
        adminMaterialService.getMaterials(),
      ]);
      setLines(lineData || []);
      setSummary(summaryData);
      setMaterials(materialData || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load materials');
    }
  }, [project.id, toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (project.project_type === 'own_design' && project.source_product_id) {
      adminProductVariantService
        .getProductVariants(project.source_product_id)
        .then((v) => setSourceVariants(v || []))
        .catch(() => setSourceVariants([]));
    }
  }, [project.project_type, project.source_product_id]);

  const saveMargin = async () => {
    try {
      await atelierAdminService.editProject(project.id, {
        target_margin_percentage: targetMargin === '' ? null : Number(targetMargin),
      });
      toast.success('Target margin saved');
      onChanged();
      load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    }
  };

  const openCreate = () => {
    setEditingLineId(null);
    setFormRaw(EMPTY_MATERIAL_LINE);
    setIsDirty(false);
    setShowModal(true);
  };
  const openEdit = (line) => {
    setEditingLineId(line.id);
    setFormRaw({
      material_id: line.material_id,
      process: line.process || '',
      quantity_per_unit: String(line.quantity_per_unit),
      wastage_percentage: String(line.wastage_percentage),
      unit_cost: String(line.unit_cost),
      currency: line.currency,
      exchange_rate: String(line.exchange_rate),
      customs_duty_percentage: String(line.customs_duty_percentage),
      freight_cost_per_unit: String(line.freight_cost_per_unit),
      color_or_variant_note: line.color_or_variant_note || '',
      lot_number: line.lot_number || '',
      notes: line.notes || '',
    });
    // The material may have since been removed from the active catalog — keep it
    // selectable in this dropdown so editing the line doesn't silently blank it out.
    if (line.material_id && !materials.some((m) => m.id === line.material_id)) {
      setMaterials((prev) => [
        ...prev,
        { id: line.material_id, name: line.material_name || 'Deleted material', category: line.material_category },
      ]);
    }
    setIsDirty(false);
    setShowModal(true);
  };

  const handleMaterialChange = async (materialId) => {
    setForm((prev) => ({ ...prev, material_id: materialId }));
    if (!materialId || editingLineId) return;
    try {
      const prefill = await adminMaterialService.getMaterialPrefill(materialId);
      setForm((prev) => ({
        ...prev,
        process: prefill.process || prev.process,
        unit_cost: prefill.unit_cost != null ? String(prefill.unit_cost) : prev.unit_cost,
        currency: prefill.currency || prev.currency,
        exchange_rate: prefill.exchange_rate != null ? String(prefill.exchange_rate) : prev.exchange_rate,
        customs_duty_percentage:
          prefill.customs_duty_percentage != null
            ? String(prefill.customs_duty_percentage)
            : prev.customs_duty_percentage,
        freight_cost_per_unit:
          prefill.freight_cost_per_unit != null ? String(prefill.freight_cost_per_unit) : prev.freight_cost_per_unit,
      }));
    } catch {
      /* pre-fill convenience only */
    }
  };

  const handleSave = async () => {
    if (!form.material_id || !form.quantity_per_unit || form.unit_cost === '') {
      toast.error('Material, quantity, and unit cost are required');
      return;
    }
    const payload = {
      material_id: form.material_id,
      process: form.process || null,
      quantity_per_unit: Number(form.quantity_per_unit),
      wastage_percentage: Number(form.wastage_percentage || 0),
      unit_cost: Number(form.unit_cost),
      currency: form.currency,
      exchange_rate: Number(form.exchange_rate || 1),
      customs_duty_percentage: Number(form.customs_duty_percentage || 0),
      freight_cost_per_unit: Number(form.freight_cost_per_unit || 0),
      color_or_variant_note: form.color_or_variant_note.trim() || null,
      lot_number: form.lot_number.trim() || null,
      notes: form.notes.trim() || null,
    };
    setSaving(true);
    try {
      if (editingLineId) {
        await atelierAdminService.updateProjectMaterial(project.id, editingLineId, payload);
        toast.success('Material line updated');
      } else {
        await atelierAdminService.addProjectMaterial(project.id, payload);
        toast.success('Material line added');
      }
      setIsDirty(false);
      setShowModal(false);
      await load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (line) => {
    if (!window.confirm('Delete this material line? This cannot be undone.')) return;
    try {
      await atelierAdminService.deleteProjectMaterial(project.id, line.id);
      toast.success('Material line removed');
      await load();
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  // X button and footer Cancel both dismiss the modal — confirm first if
  // there's anything to lose. The internal setShowModal(false) call after a
  // successful save (end of handleSave) bypasses this deliberately.
  const handleCloseClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setIsDirty(false);
    setShowModal(false);
  };

  const handleCopyFromProduct = async () => {
    if (!copyVariantId) return;
    try {
      await atelierAdminService.copyMaterialsFromProduct(project.id, copyVariantId);
      toast.success('Materials copied from source product — adjust for wholesale sourcing');
      setCopyVariantId('');
      await load();
    } catch (e) {
      toast.error(e.message || 'Copy failed');
    }
  };

  const materialName = (id) => materials.find((m) => m.id === id)?.name || '—';

  return (
    <div className="space-y-4">
      {sourceVariants.length > 0 && (
        <section className="bg-card border rounded-lg p-4">
          <h3 className="font-medium mb-2">Copy from source product</h3>
          <p className="text-sm text-muted-foreground mb-2">
            This is an own-design project — start from a representative variant's D2C bill of materials, then adjust for
            wholesale sourcing / this batch's actual size mix.
          </p>
          <div className="flex gap-2">
            <select
              value={copyVariantId}
              onChange={(e) => setCopyVariantId(e.target.value)}
              className="flex-1 border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select variant…</option>
              {sourceVariants.map((v) => (
                <option key={v.id} value={v.id}>
                  {[v.size, v.color].filter(Boolean).join(' / ') || v.sku}
                </option>
              ))}
            </select>
            <button
              onClick={handleCopyFromProduct}
              disabled={!copyVariantId}
              className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-40 flex items-center gap-1"
            >
              <Copy size={14} /> Copy
            </button>
          </div>
        </section>
      )}

      <section className="bg-card border rounded-lg p-4">
        <h3 className="font-medium mb-2">Cost summary</h3>
        {summary && (
          <div className="text-sm space-y-1 mb-3">
            <div>
              Total landed material cost per unit:{' '}
              <span className="font-medium">₹{summary.total_cost_per_unit_inr?.toFixed(2)}</span>
            </div>
            {summary.suggested_unit_price_inr != null && (
              <div className="bg-green-50 dark:bg-green-950/50 border border-green-200 dark:border-green-900 rounded-lg p-2 mt-2">
                Suggested unit price at {summary.target_margin_percentage}% margin:{' '}
                <span className="font-medium">₹{summary.suggested_unit_price_inr.toFixed(2)}</span>
                <span className="text-xs text-muted-foreground block">
                  Enter this as the Unit Price on the Quote &amp; Invoices tab.
                </span>
              </div>
            )}
          </div>
        )}
        <div className="flex items-end gap-2">
          <label className="text-sm">
            Target margin %
            <input
              type="number"
              step="0.1"
              value={targetMargin}
              onChange={(e) => setTargetMargin(e.target.value)}
              className="block w-32 border rounded-lg px-3 py-2 mt-1"
            />
          </label>
          <button onClick={saveMargin} className="px-3 py-2 rounded-lg bg-muted text-sm">
            Save margin
          </button>
        </div>
      </section>

      <section className="bg-card border rounded-lg p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="font-medium">Material lines</h3>
          <button
            onClick={openCreate}
            className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm flex items-center gap-1"
          >
            <Plus size={14} /> Add line
          </button>
        </div>
        {lines.length === 0 ? (
          <p className="text-muted-foreground/70 text-sm">No material lines yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b">
                <tr>
                  <th className="text-left py-2 font-medium">Material</th>
                  <th className="text-left py-2 font-medium">Qty / Unit</th>
                  <th className="text-left py-2 font-medium">Unit Cost</th>
                  <th className="text-left py-2 font-medium">Landed Cost</th>
                  <th className="text-left py-2 font-medium">Sourced From</th>
                  <th className="text-right py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <tr key={line.id} className="border-b last:border-b-0">
                    <td className="py-2">
                      {line.material_name || materialName(line.material_id)}
                      {line.process && (
                        <span className="text-xs text-muted-foreground/70 capitalize">
                          {' '}
                          — {line.process.replace(/_/g, ' ')}
                        </span>
                      )}
                    </td>
                    <td className="py-2">{line.quantity_per_unit}</td>
                    <td className="py-2">
                      {line.currency} {line.unit_cost}
                    </td>
                    <td className="py-2">₹{line.landed_unit_cost_inr?.toFixed(2)}</td>
                    <td className="py-2 text-muted-foreground">{line.vendor_name || '—'}</td>
                    <td className="py-2">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => openEdit(line)}
                          className="p-1 text-muted-foreground hover:text-foreground"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(line)}
                          className="p-1 text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showModal && (
        <Modal
          maxWidth="xl"
          onClose={handleCloseClick}
          className="rounded-lg"
          closeOnBackdrop={false}
          hasUnsavedChanges={isDirty}
        >
          <div className="sticky top-0 bg-card border-b px-6 py-4 flex justify-between items-center">
            <h2 className="text-lg font-medium">{editingLineId ? 'Edit Material Line' : 'Add Material Line'}</h2>
            <button onClick={handleCloseClick} className="text-muted-foreground hover:text-foreground">
              <X size={20} />
            </button>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Material *</label>
              <select
                value={form.material_id}
                onChange={(e) => handleMaterialChange(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Select material…</option>
                {materials.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <label className="text-sm block">
              Process
              <select
                value={form.process}
                onChange={(e) => setForm({ ...form, process: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 mt-1"
              >
                {MATERIAL_PROCESSES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="text-sm">
                Quantity per Unit *
                <input
                  type="number"
                  step="0.0001"
                  value={form.quantity_per_unit}
                  onChange={(e) => setForm({ ...form, quantity_per_unit: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                />
              </label>
              <label className="text-sm">
                Wastage %
                <input
                  type="number"
                  step="0.01"
                  value={form.wastage_percentage}
                  onChange={(e) => setForm({ ...form, wastage_percentage: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                />
              </label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <label className="text-sm">
                Unit Cost *
                <input
                  type="number"
                  step="0.01"
                  value={form.unit_cost}
                  onChange={(e) => setForm({ ...form, unit_cost: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                />
              </label>
              <label className="text-sm">
                Currency
                <input
                  type="text"
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                />
              </label>
              <label className="text-sm">
                Exchange Rate → INR
                <input
                  type="number"
                  step="0.000001"
                  value={form.exchange_rate}
                  onChange={(e) => setForm({ ...form, exchange_rate: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                />
              </label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="text-sm">
                Customs Duty %
                <input
                  type="number"
                  step="0.01"
                  value={form.customs_duty_percentage}
                  onChange={(e) => setForm({ ...form, customs_duty_percentage: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                />
              </label>
              <label className="text-sm">
                Freight Cost / Unit (INR)
                <input
                  type="number"
                  step="0.01"
                  value={form.freight_cost_per_unit}
                  onChange={(e) => setForm({ ...form, freight_cost_per_unit: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                />
              </label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="text-sm">
                Color / Variant Note
                <input
                  type="text"
                  value={form.color_or_variant_note}
                  onChange={(e) => setForm({ ...form, color_or_variant_note: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                />
              </label>
              <label className="text-sm">
                Lot / Batch Number
                <input
                  type="text"
                  value={form.lot_number}
                  onChange={(e) => setForm({ ...form, lot_number: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                />
              </label>
            </div>
            <label className="text-sm block">
              Notes
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="w-full border rounded-lg px-3 py-2 mt-1"
              />
            </label>
          </div>
          <div className="sticky bottom-0 bg-card border-t px-6 py-4 flex justify-end gap-2">
            <button onClick={handleCloseClick} disabled={saving} className="px-4 py-2 rounded-lg border">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground disabled:opacity-40"
            >
              {saving ? 'Saving…' : 'Save Line'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function QuoteInvoices({ project, onChanged }) {
  const toast = useToast();
  const [unit, setUnit] = useState(project.unit_price || '');
  const [sample, setSample] = useState(project.sample_cost || '');
  const [sampleLead, setSampleLead] = useState(project.sample_lead_days || '');
  const [lead, setLead] = useState(project.expected_lead_time_days || '');
  const [preview, setPreview] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(false);
  // Touched-since-load flag — these fields have no discard/cancel affordance
  // (always visible), so they only need to register with the app-wide guard.
  const [isDirty, setIsDirty] = useState(false);
  useRegisterUnsavedChanges(isDirty);

  const loadInvoices = useCallback(async () => {
    try {
      setInvoices(await atelierAdminService.listProjectInvoices(project.id));
    } catch {
      /* ignore */
    }
  }, [project.id]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const payload = () => ({
    unit_price_inr: unit === '' ? null : Number(unit),
    sample_cost_inr: sample === '' ? null : Number(sample),
    sample_lead_days: sampleLead === '' ? null : Number(sampleLead),
    expected_lead_time_days: lead === '' ? null : Number(lead),
  });

  const doPreview = async () => {
    try {
      setPreview(await atelierAdminService.quotePreview(project.id, payload()));
    } catch (e) {
      toast.error(e.message || 'Preview failed');
    }
  };
  const saveQuote = async () => {
    setBusy(true);
    try {
      await atelierAdminService.setQuote(project.id, payload());
      toast.success('Quote saved');
      setIsDirty(false);
      onChanged();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };
  const gen = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      loadInvoices();
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <section className="bg-card border rounded-lg p-4 space-y-2">
        <h3 className="font-medium mb-1">
          Quote <span className="text-xs text-muted-foreground/70">(prices in INR base)</span>
        </h3>
        <label className="block text-sm">
          Unit price (INR)
          <input
            value={unit}
            onChange={(e) => {
              setUnit(e.target.value);
              setIsDirty(true);
            }}
            type="number"
            className="w-full border rounded-lg px-3 py-2 mt-1"
            placeholder="auto for own-design"
          />
        </label>
        <label className="block text-sm">
          Sample cost (INR)
          <input
            value={sample}
            onChange={(e) => {
              setSample(e.target.value);
              setIsDirty(true);
            }}
            type="number"
            className="w-full border rounded-lg px-3 py-2 mt-1"
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-sm">
            Sample lead (days)
            <input
              value={sampleLead}
              onChange={(e) => {
                setSampleLead(e.target.value);
                setIsDirty(true);
              }}
              type="number"
              className="w-full border rounded-lg px-3 py-2 mt-1"
            />
          </label>
          <label className="block text-sm">
            Production lead (days)
            <input
              value={lead}
              onChange={(e) => {
                setLead(e.target.value);
                setIsDirty(true);
              }}
              type="number"
              className="w-full border rounded-lg px-3 py-2 mt-1"
            />
          </label>
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={doPreview} className="px-3 py-2 rounded-lg bg-muted text-sm">
            Preview
          </button>
          <button
            disabled={busy}
            onClick={saveQuote}
            className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-40"
          >
            Save quote
          </button>
        </div>
        {preview && (
          <div className="mt-3 text-sm bg-muted/50 rounded-lg p-3">
            <div>
              Unit: {preview.symbol}
              {preview.unit_price} · Total: {preview.symbol}
              {preview.production_total} ({preview.currency})
            </div>
            <div>
              Deposit 50%: {preview.symbol}
              {preview.deposit_amount} · Balance 50%: {preview.symbol}
              {preview.balance_amount}
            </div>
            <div>
              Sample: {preview.symbol}
              {preview.sample_cost}
            </div>
          </div>
        )}
      </section>

      <section className="bg-card border rounded-lg p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="font-medium">Invoices</h3>
          <div className="flex gap-2">
            <button
              disabled={busy}
              onClick={() => gen(() => atelierAdminService.generate5050(project.id), '50/50 invoices created')}
              className="px-2 py-1 rounded-md bg-primary text-primary-foreground text-xs disabled:opacity-40"
            >
              Generate 50/50
            </button>
            <button
              disabled={busy}
              onClick={() =>
                gen(() => atelierAdminService.createInvoice(project.id, 'sample'), 'Sample invoice created')
              }
              className="px-2 py-1 rounded-md bg-muted text-xs disabled:opacity-40"
            >
              Sample
            </button>
          </div>
        </div>
        <ul className="space-y-2">
          {invoices.map((i) => (
            <li key={i.id} className="border rounded-lg p-3 text-sm">
              <div className="flex justify-between">
                <span className="font-mono">{i.invoice_number}</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs ${i.status === 'paid' ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300' : 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300'}`}
                >
                  {i.status}
                </span>
              </div>
              <div className="text-muted-foreground capitalize">
                {i.milestone} · {i.currency} {i.amount}
              </div>
              <div className="flex gap-3 mt-1">
                {i.pdf_url && (
                  <a
                    href={i.pdf_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    PDF
                  </a>
                )}
                {i.status !== 'paid' && (
                  <button
                    onClick={() => gen(() => atelierAdminService.markInvoicePaid(i.id, 'bank_transfer'), 'Marked paid')}
                    className="text-green-700 dark:text-green-300 hover:underline"
                  >
                    Mark paid
                  </button>
                )}
              </div>
            </li>
          ))}
          {invoices.length === 0 && <li className="text-muted-foreground/70 text-sm">No invoices yet</li>}
        </ul>
      </section>
    </div>
  );
}

function Messages({ projectId }) {
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setMessages(await atelierAdminService.listMessages(projectId));
    } catch {
      /* ignore */
    }
  }, [projectId]);
  useEffect(() => {
    load();
  }, [load]);

  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await atelierAdminService.postMessage(projectId, body.trim());
      setBody('');
      load();
    } catch (e) {
      toast.error(e.message || 'Send failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="bg-card border rounded-lg p-4 space-y-3 mb-3 max-h-[50vh] overflow-y-auto">
        {messages.map((m) => (
          <div key={m.id} className={`text-sm ${m.sender_type === 'admin' ? 'text-right' : ''}`}>
            <div
              className={`inline-block rounded-lg px-3 py-2 ${m.sender_type === 'admin' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
            >
              {m.body}
            </div>
            <div className="text-xs text-muted-foreground/70 mt-0.5">
              {m.sender_type} · {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
            </div>
          </div>
        ))}
        {messages.length === 0 && <p className="text-muted-foreground/70 text-sm">No messages</p>}
      </div>
      <div className="flex gap-2">
        <input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Reply to the buyer…"
          className="flex-1 border rounded-lg px-3 py-2 text-sm"
        />
        <button
          disabled={busy}
          onClick={send}
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-40"
        >
          Send
        </button>
      </div>
    </div>
  );
}

function Files({ projectId }) {
  const toast = useToast();
  const [files, setFiles] = useState([]);
  const [kind, setKind] = useState('sample_photo');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setFiles(await atelierAdminService.listAttachments(projectId));
    } catch {
      /* ignore */
    }
  }, [projectId]);
  useEffect(() => {
    load();
  }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      await atelierAdminService.uploadAttachment(projectId, kind, file);
      toast.success('Uploaded');
      load();
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const remove = async (id) => {
    try {
      await atelierAdminService.deleteAttachment(id);
      load();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-2 mb-4">
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          {['sample_photo', 'design_file', 'tech_pack', 'reference_image', 'other'].map((k) => (
            <option key={k} value={k}>
              {k.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
        <label className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm cursor-pointer">
          {busy ? 'Uploading…' : 'Upload file'}
          <input type="file" className="hidden" onChange={upload} disabled={busy} />
        </label>
      </div>
      <ul className="space-y-2">
        {files.map((f) => (
          <li key={f.id} className="flex justify-between border rounded-lg px-3 py-2 text-sm">
            <span className="capitalize">
              {f.kind.replace(/_/g, ' ')} · {f.original_filename || 'file'}{' '}
              <span className="text-muted-foreground/70">({f.uploaded_by_type})</span>
            </span>
            <span className="flex gap-3">
              <a
                href={f.file_url}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                View
              </a>
              <button onClick={() => remove(f.id)} className="text-red-600 dark:text-red-400 hover:underline">
                Delete
              </button>
            </span>
          </li>
        ))}
        {files.length === 0 && <li className="text-muted-foreground/70 text-sm">No files</li>}
      </ul>
    </div>
  );
}
